import { useCallback, useEffect, useRef } from 'react';
import { useAppStore } from '@stores';

export class HistoryController {
  private static instance: HistoryController | null = null;
  private isActive = false;
  private interceptors: Array<{ handler: () => boolean; id: string; }> = [];
  private statePrefix = 'hist_ctrl_';
  private ids: string[] = [];

  public static getInstance(): HistoryController {
    if (!HistoryController.instance) {
      HistoryController.instance = new HistoryController();
    }
    return HistoryController.instance;
  }

  private constructor() {}

  public initialize(): void {
    if (!this.isActive) {
      window.addEventListener('popstate', this.handlePopState);
      this.isActive = true;
    }
  }

  public destroy(): void {
    window.removeEventListener('popstate', this.handlePopState);
    this.isActive = false;
    this.interceptors = [];
  }

  private handlePopState = (_event: PopStateEvent): void => {
    if (useAppStore.getState().tab === 'combat') return;

    for (const id of this.ids.reverse()) {
      const index = this.interceptors.findIndex(i => i.id === id);

      if (index !== -1) {
        const shouldBlock = this.interceptors[index].handler();
        if (shouldBlock) return;
      } else {
        this.ids = this.ids.filter(i => i !== id);
      }
    }

    window.history.back();
  };

  public registerHandler(id: string, handler: () => boolean): () => void {
    if (this.ids.includes(id)) return () => {};

    this.interceptors.push({ id, handler });
    this.ids.push(id);

    this.initialize();

    window.history.pushState({ key: `${this.statePrefix}${id}` }, '');

    return () => this.removeHandler(id);
  }

  private removeHandler(id: string): void {
    this.ids = this.ids.filter(i => i !== id);

    const index = this.interceptors.findIndex(i => i.id === id);
    if (index !== -1) this.interceptors.splice(index, 1);
  }
}

/**
 * React hook for handling back navigation with modals, drawers, etc.
 * 
 * This hook will:
 * 1. Add a history entry when the component mounts or isOpen becomes true
 * 2. When back is pressed, execute the onBack callback
 * 3. If the callback returns true, it will block navigation
 * 4. If the callback returns false, it will allow navigation
 * 
 * @param onBack Callback to execute when back is pressed, should return true to block navigation
 * @param deps Dependencies that should trigger a re-registration of the handler
 * @param options Optional configuration
 */
export function useHistoryInterceptor(
  onBack: () => boolean,
  options: { disabled: boolean; id?: string; }
): void {
  const { id: providedId, disabled = false } = options;

  const idRef = useRef<string>(
    providedId || `handler-${Math.random().toString(36).substring(2, 9)}`
  );

  const callbackRef = useRef(onBack);
  callbackRef.current = onBack;

  const stableCallback = useCallback(() => {
    return callbackRef.current();
  }, []);

  useEffect(() => {
    if (disabled) return;

    const controller = HistoryController.getInstance();
    const cleanup = controller.registerHandler(idRef.current, stableCallback);

    return cleanup;
  }, [stableCallback, disabled, options]);
}